class RCScroller extends HTMLElement {

  constructor() {
    super();
    this._track = null;
    this._anim = null;
    this._pos = 0;
    this._lastTs = 0;
    this._speed = 30; // px/s
    this._pauseOnHover = true;
    this._clones = [];
    this._onMouseEnter = () => this._stop();
    this._onMouseLeave = () => this._start();
    this._scheduleRecalc = this._scheduleRecalc.bind(this);
    this._resizeObserver = null;
    this._recalcRaf = null;
    this._recalcRunning = false;
    this._lastTrackW = -1;
    this._lastContainerW = -1;
  };

  connectedCallback() {
    if (this.dataset.enabled !== 'true') return;
    this._init();
  };

  disconnectedCallback() {
    this._cleanup();
  };

  _init() {
    this._track = this.querySelector('.rc-track');
    if(!this._track) return;
    const ds = this.dataset;
    const speed = Number(ds.speed || this._speed);
    this._speed = isNaN(speed) ? this._speed : speed;
    this._pauseOnHover = String(ds.pause || 'true').toLowerCase() !== 'false'
      && window.matchMedia('(hover: hover)').matches;

    // Ensure base styles
    this.style.overflow = 'hidden';
    this._track.style.display = 'flex';
    this._track.style.willChange = 'transform';

    // Wrap original in a container for single transform
    const wrapper = document.createElement('div');
    wrapper.style.display = 'flex';
    wrapper.style.willChange = 'transform';
    wrapper.className = 'rc-scroll-wrapper';
    this.insertBefore(wrapper, this._track);
    wrapper.appendChild(this._track);
    this._wrapper = wrapper;

    this._recalc();

    if(this._pauseOnHover) {
      this.addEventListener('mouseenter', this._onMouseEnter);
      this.addEventListener('mouseleave', this._onMouseLeave);
    };

    // Size the loop from the element itself. window.resize is intentionally
    // not used: mobile browsers fire it when the URL bar shows/hides on scroll.
    try {
      this._resizeObserver = new ResizeObserver(this._scheduleRecalc);
      this._resizeObserver.observe(this);
      this._resizeObserver.observe(this._track);
    } catch (e) {};
    this._start();
  };

  _scheduleRecalc() {
    if (this._recalcRaf) return;
    this._recalcRaf = requestAnimationFrame(() => {
      this._recalcRaf = null;
      this._recalc();
    });
  };

  _measure() {
    this._track.style.flex = '0 0 auto';
    return {
      trackW: this._track.getBoundingClientRect().width,
      containerW: this.getBoundingClientRect().width
    };
  };

  _recalc() {
    if (!this._track || !this._wrapper || this._recalcRunning) return;
    this._recalcRunning = true;

    try {
      const { trackW, containerW } = this._measure();

      // Ignore height-only changes (mobile URL bar) and subpixel noise.
      if (
        this._clones.length > 0 &&
        Math.abs(trackW - this._lastTrackW) < 1 &&
        Math.abs(containerW - this._lastContainerW) < 1
      ) {
        return;
      };

      this._lastTrackW = trackW;
      this._lastContainerW = containerW;

      const savedPos = this._pos;

      if(this._clones.length) {
        this._clones.forEach((c) => c.remove());
        this._clones = [];
      };

      this._loopWidth = trackW;

      if(trackW > 0 && containerW > 0) {
        const repeats = Math.max(2, Math.ceil(containerW / trackW) + 1);
        for(let i = 0; i < repeats - 1; i++) {
          const clone = this._track.cloneNode(true);
          clone.classList.add('rc-track-clone');
          clone.style.flex = '0 0 auto';
          this._wrapper.appendChild(clone);
          this._clones.push(clone);
        };
      };

      this._pos = this._loopWidth > 0 ? savedPos % this._loopWidth : 0;
      this._wrapper.style.transform = `translate3d(${-this._pos}px, 0, 0)`;
    } finally {
      this._recalcRunning = false;
    };
  };

  _start() {
    if(this._anim) return;
    const step = (ts) => {
      if(!this._lastTs) this._lastTs = ts;
      const dt = Math.min((ts - this._lastTs) / 1000, 0.05);
      this._lastTs = ts;
      this._pos += this._speed * dt;
      if(this._loopWidth > 0) {
        this._pos %= this._loopWidth;
      };
      this._wrapper.style.transform = `translate3d(${-this._pos}px, 0, 0)`;
      this._anim = requestAnimationFrame(step);
    };
    this._anim = requestAnimationFrame(step);
  };

  _stop() {
    if(this._anim) {
      cancelAnimationFrame(this._anim);
      this._anim = null;
      this._lastTs = 0;
    };
  };

  _cleanup() {
    this._stop();
    if (this._recalcRaf) {
      cancelAnimationFrame(this._recalcRaf);
      this._recalcRaf = null;
    };
    this.removeEventListener('mouseenter', this._onMouseEnter);
    this.removeEventListener('mouseleave', this._onMouseLeave);
    if(this._resizeObserver) {
      try { this._resizeObserver.disconnect(); } catch (e) {}
      this._resizeObserver = null;
    };
  };
};

if(!customElements.get('rc-scroller')) {
  customElements.define('rc-scroller', RCScroller);
};
